import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import TypeSelect from "@/components/TypeSelect";
import { allTypes } from "@/lib/entities";
import { defaultMagSize } from "@/lib/ammo";
import { Trash2, Sparkles, Package, Laugh } from "lucide-react";

const fmtContents = (arr) => (arr || []).map((c) => (c.qty && c.qty > 1 ? `${c.qty} x ${c.name}` : c.name)).join("\n");
const parseContents = (txt) => (txt || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
  // Mags -> rounds: "3 mags of 30 5.56mm rounds", "3 mag x 30 5.56mm" -> 90 rounds
  let mg = l.match(/^(\d+)\s*mags?\s*(?:of|x|\*|×|@)?\s*(\d+)\s+(.+)$/i);
  if (mg) return { name: mg[3].trim(), qty: parseInt(mg[1], 10) * parseInt(mg[2], 10), unit: "rounds" };
  // Leading qty: "4 x bandage", "4x bandage", "4 * bandage"
  let m = l.match(/^(\d+)\s*[x*×]\s*(.+)$/i);
  if (m) return { name: m[2].trim(), qty: parseInt(m[1], 10) };
  // Trailing qty: "bandage x4", "bandage x 4", "bandage (4)", "bandage - 4", "bandage: 4"
  m = l.match(/^(.+?)\s*(?:[x*×]\s*|\(\s*|[-–:]\s*)(\d+)\)?$/i);
  if (m) return { name: m[1].trim(), qty: parseInt(m[2], 10) };
  return { name: l, qty: 1 };
});

export default function EntityDialog({ open, onOpenChange, entity, onSave, onDelete, customTypes = [], onAddType }) {
  const isEdit = !!entity;
  const [form, setForm] = useState({ name: "", type: "character", description: "", detail: "", voice: "", nickname: "", included: false, comic: false, calibre: "", mag_size: "" });
  const [contentsText, setContentsText] = useState("");

  useEffect(() => {
    if (open) {
      setForm(entity
        ? { name: entity.name, type: entity.type, description: entity.description || "", detail: entity.detail || "", voice: entity.voice || "", nickname: entity.nickname || "", included: !!entity.included, comic: !!entity.comic, calibre: entity.calibre || "", mag_size: entity.mag_size ?? "" }
        : { name: "", type: "character", description: "", detail: "", voice: "", nickname: "", included: false, comic: false, calibre: "", mag_size: "" });
      setContentsText(fmtContents(entity?.contents));
    }
  }, [open, entity]);

  const isKit = (customTypes || []).some((t) => t.key === form.type && t.is_kit);

  const save = () => {
    if (!form.name.trim()) return;
    const magNum = form.mag_size === "" || form.mag_size == null ? null : Number(form.mag_size);
    onSave({ ...form, mag_size: Number.isNaN(magNum) ? null : magNum, contents: isKit ? parseContents(contentsText) : undefined });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover" data-testid="entity-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {isEdit ? "Update vital detail" : "Add vital detail"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Changes here update this detail everywhere in your codex — e.g. swap a weapon and it's remembered globally."
              : "Track a character, weapon, location, object or faction in your story memory."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-1">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Mr Bloggs" data-testid="entity-name-input" autoFocus />
            </div>
            <div className="space-y-1.5 col-span-1">
              <Label>Type</Label>
              <TypeSelect value={form.type} onChange={(v) => setForm({ ...form, type: v })}
                types={allTypes(customTypes)} onAddType={onAddType} testid="entity-type-select" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="A weathered ex-soldier turned reluctant hero." rows={2} data-testid="entity-desc-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Key detail <span className="text-muted-foreground font-normal">(model, material, colour…)</span></Label>
            <Input value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })}
              placeholder="MZK-1832-PWR" data-testid="entity-detail-input" className="font-mono-code" />
          </div>
          {form.type === "character" && (
            <div className="space-y-1.5">
              <Label>Nickname / alias <span className="text-muted-foreground font-normal">(also recognised in your text)</span></Label>
              <Input value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                placeholder="Bloggsy" data-testid="entity-nickname-input" />
            </div>
          )}
          {form.type === "character" && (
            <div className="space-y-1.5">
              <Label>How they speak <span className="text-muted-foreground font-normal">(optional, keeps their voice distinct)</span></Label>
              <Textarea value={form.voice} onChange={(e) => setForm({ ...form, voice: e.target.value })}
                placeholder="Blunt, short sentences, swears under pressure, never talks about his family, deflects with dark jokes."
                rows={2} data-testid="entity-voice-input" />
            </div>
          )}
          {form.type === "weapon" && (
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-border bg-secondary/30 p-3" data-testid="weapon-ammo-fields">
              <div className="space-y-1.5">
                <Label className="text-xs">Calibre / ammo type</Label>
                <Input value={form.calibre}
                  onChange={(e) => {
                    const cal = e.target.value;
                    setForm((f) => ({ ...f, calibre: cal, mag_size: (f.mag_size === "" || f.mag_size == null) ? (defaultMagSize(cal) || "") : f.mag_size }));
                  }}
                  placeholder="5.56×45 / 9mm / arrows" data-testid="weapon-calibre-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Magazine size <span className="text-muted-foreground font-normal">(rounds)</span></Label>
                <Input type="number" min={0} value={form.mag_size}
                  onChange={(e) => setForm({ ...form, mag_size: e.target.value })}
                  placeholder="30" data-testid="weapon-magsize-input" />
                <p className="text-[11px] text-muted-foreground">Auto-filled from calibre — override for SAWs, drums, etc.</p>
              </div>
            </div>
          )}
          {isKit && (
            <div className="space-y-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
              <Label className="flex items-center gap-1.5"><Package className="w-4 h-4 text-emerald-500" /> Kit contents <span className="text-muted-foreground font-normal">(one per line, e.g. "2 x bandage")</span></Label>
              <Textarea value={contentsText} onChange={(e) => setContentsText(e.target.value)}
                placeholder={"1 x L85 rifle\n3 mags of 30 5.56mm rounds\n4 x bandage\n1 x tourniquet"} rows={5}
                data-testid="entity-contents-input" className="font-mono-code text-sm" />
              <p className="text-xs text-muted-foreground">This list is the single source. Tip: write ammo as rounds ("90 x 5.56mm rounds") or as mags — "<span className="font-mono-code">3 mags of 30 5.56mm rounds</span>" auto-becomes 90 rounds. Drag this kit onto the manuscript to drop the list into your story.</p>
            </div>
          )}
          {form.type === "character" && (
            <div className="flex items-center justify-between rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-3" data-testid="entity-comic-row">
              <div>
                <Label className="cursor-pointer flex items-center gap-1.5"><Laugh className="w-4 h-4 text-fuchsia-400" /> One of the funny ones</Label>
                <p className="text-xs text-muted-foreground">Cluster the wit &amp; banter around this character when humour is on.</p>
              </div>
              <Switch checked={!!form.comic} onCheckedChange={(v) => setForm({ ...form, comic: v })} data-testid="entity-comic-switch" />
            </div>
          )}
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <Label className="cursor-pointer">Mark as included</Label>
              <p className="text-xs text-muted-foreground">Flag this detail as woven into the story.</p>
            </div>
            <Switch checked={form.included} onCheckedChange={(v) => setForm({ ...form, included: v })} data-testid="entity-included-switch" />
          </div>
        </div>

        <DialogFooter className="flex sm:justify-between gap-2">
          {isEdit ? (
            <Button variant="outline" onClick={() => onDelete(entity)} data-testid="entity-delete-button"
              className="text-destructive hover:text-destructive gap-2">
              <Trash2 className="w-4 h-4" /> Delete
            </Button>
          ) : <span />}
          <Button onClick={save} disabled={!form.name.trim()} data-testid="entity-save-button">
            {isEdit ? "Save changes" : "Add to codex"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
